import { useEffect, useState } from 'react';
import { Box, Button, Dialog, Snackbar, Typography } from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { useTranslation } from 'react-i18next';
import {
  getIssueFeedbackApi,
  submitIssueFeedbackApi,
} from '../../api/learners';

interface ReportIssueButtonProps {
  questionId: string;
  sessionId: string;
  questionNumber?: number;
  conceptName?: string;
  variant?:
    | 'question'
    | 'all' /** Question page: question-only reasons. Review/overlay: every reason. */;
}

type SubmitStatus = 'idle' | 'submitting' | 'error';

const QUESTION_REPORT_REASONS: { id: string; label: string }[] = [
  { id: 'unclear', label: 'PAL_RI_REASON_UNCLEAR' },
  { id: 'no-correct', label: 'PAL_RI_REASON_NO_CORRECT' },
  { id: 'duplicate-options', label: 'PAL_RI_REASON_DUPLICATE_OPTIONS' },
  { id: 'typo', label: 'PAL_RI_REASON_TYPO' },
  { id: 'media', label: 'PAL_RI_REASON_MEDIA' },
  { id: 'other', label: 'PAL_RI_REASON_OTHER' },
];

const REVIEW_REPORT_REASONS: { id: string; label: string }[] = [
  { id: 'wrong-answer', label: 'PAL_RI_REASON_WRONG_ANSWER' },
  { id: 'wrong-explanation', label: 'PAL_RI_REASON_WRONG_EXPLANATION' },
  { id: 'contradicts', label: 'PAL_RI_REASON_CONTRADICTS' },
];

const ALL_REPORT_REASONS: { id: string; label: string }[] = [
  ...QUESTION_REPORT_REASONS.slice(0, 3),
  ...REVIEW_REPORT_REASONS,
  ...QUESTION_REPORT_REASONS.slice(3),
];

export const ReportIssueButton = ({
  questionId,
  sessionId,
  questionNumber,
  conceptName,
  variant = 'all',
}: ReportIssueButtonProps) => {
  const { t } = useTranslation();
  const reportReasons =
    variant === 'question' ? QUESTION_REPORT_REASONS : ALL_REPORT_REASONS;
  const defaultReason = reportReasons[0].id;
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState(defaultReason);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [toastOpen, setToastOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedReason, setSavedReason] = useState<string | null>(null);
  const [savedComment, setSavedComment] = useState('');

  useEffect(() => {
    setOpen(false);
    setSelectedReason(defaultReason);
    setComment('');
    setStatus('idle');
    setSavedReason(null);
    setSavedComment('');

    if (!questionId) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    getIssueFeedbackApi(questionId)
      .then((feedback) => {
        if (!active) return;
        const latest = feedback?.[0];
        setSavedReason(latest?.issue_reason ?? null);
        setSavedComment(latest?.comment ?? '');
      })
      .catch((err) => {
        if (!active) return;
        console.error('Failed to fetch issue feedback', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [questionId, sessionId]);

  const handleOpen = () => {
    const reasonIds = reportReasons.map((reason) => reason.id);
    setSelectedReason(
      savedReason && reasonIds.includes(savedReason)
        ? savedReason
        : defaultReason
    );
    setComment(savedComment);
    setStatus('idle');
    setOpen(true);
  };

  const handleClose = () => {
    if (status === 'submitting') return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedReason || !sessionId || !questionId) return;
    const trimmedComment = comment.trim();
    try {
      setStatus('submitting');
      await submitIssueFeedbackApi(sessionId, questionId, {
        reason: selectedReason,
        comment: trimmedComment || undefined,
      });
      setSavedReason(selectedReason);
      setSavedComment(trimmedComment);
      setOpen(false);
      setStatus('idle');
      setToastOpen(true);
    } catch (err) {
      console.error('Failed to submit issue feedback', err);
      setStatus('error');
    }
  };

  const contextLabel = [
    questionNumber ? t('PAL_PP_QUESTION_CHIP', { n: questionNumber }) : null,
    conceptName || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={loading}
        disableElevation
        sx={{
          minWidth: 0,
          px: 1.5,
          py: 0.625,
          borderRadius: 100,
          bgcolor: '#fff',
          border: '1px solid #e2e8f0',
          color: '#64748b',
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          lineHeight: 1.4,
          gap: 0.75,
          boxShadow: 'none',
          flexShrink: 0,
          transform: 'translateY(0)',
          transition:
            'transform 0.2s ease, background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            bgcolor: '#fef2f2',
            borderColor: '#fecaca',
            color: '#dc2626',
            boxShadow: 'none',
            transform: 'translateY(-2px)',
            '& .report-issue-icon': {
              color: '#dc2626',
            },
          },
        }}
      >
        <FlagOutlinedIcon
          className="report-issue-icon"
          sx={{ fontSize: 16, color: '#94a3b8' }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {t('PAL_AR_REPORT_ISSUE')}
        </Typography>
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        BackdropProps={{
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
          },
        }}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 460,
            maxHeight: 'calc(100% - 32px)',
            m: 2.5,
            p: '26px 26px 22px',
            borderRadius: '18px',
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
            display: 'block',
            overflowY: 'auto',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            mb: '16px',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              bgcolor: '#fef2f2',
              border: '1.5px solid #fecaca',
              color: '#dc2626',
            }}
          >
            ⚑
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: '#0f172a',
                m: '0 0 3px',
              }}
            >
              {t('PAL_RI_TITLE')}
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}
            >
              {t('PAL_RI_SUBTITLE')}
            </Typography>
          </Box>
        </Box>

        {/* Context label */}
        {contextLabel && (
          <Box
            sx={{
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 700,
              color: '#4338ca',
              bgcolor: '#eef2ff',
              border: '1px solid #c7d2fe',
              px: '10px',
              py: '4px',
              borderRadius: '6px',
              mb: '14px',
            }}
          >
            {contextLabel}
          </Box>
        )}

        {/* Reasons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            mb: '16px',
          }}
        >
          {reportReasons.map((reason) => {
            const isSelected = selectedReason === reason.id;
            return (
              <Box
                component="label"
                key={reason.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  px: '12px',
                  py: '10px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: isSelected ? '#6366f1' : '#e2e8f0',
                  bgcolor: isSelected ? '#eef2ff' : '#fff',
                  transition: 'all 0.14s ease',
                  '&:hover': {
                    borderColor: isSelected ? '#6366f1' : '#cbd5e1',
                    bgcolor: isSelected ? '#eef2ff' : '#f8fafc',
                  },
                }}
              >
                <Box
                  component="input"
                  type="radio"
                  name="flagReason"
                  value={reason.id}
                  checked={isSelected}
                  onChange={() => setSelectedReason(reason.id)}
                  sx={{ accentColor: '#6366f1', cursor: 'pointer', m: 0 }}
                />
                <Typography
                  sx={{ fontSize: 13, fontWeight: 500, color: '#334155' }}
                >
                  {t(reason.label)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Note */}
        <Typography
          component="label"
          htmlFor="flagNote"
          sx={{
            display: 'block',
            fontSize: 12,
            fontWeight: 600,
            color: '#64748b',
            mb: '6px',
          }}
        >
          {t('PAL_RI_NOTE_LABEL')}
        </Typography>
        <Box
          component="textarea"
          id="flagNote"
          rows={3}
          value={comment}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setComment(e.target.value)
          }
          placeholder={t('PAL_RI_COMMENT_PLACEHOLDER')}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            p: '10px 12px',
            borderRadius: '10px',
            border: '1.5px solid #e2e8f0',
            fontFamily: 'inherit',
            fontSize: 13,
            color: '#1e293b',
            resize: 'vertical',
            mb: '18px',
            '&:focus': {
              outline: 'none',
              borderColor: '#6366f1',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.12)',
            },
            '&::placeholder': { color: '#94a3b8' },
          }}
        />

        {status === 'error' && (
          <Typography sx={{ fontSize: 12, color: '#dc2626', mb: '12px' }}>
            {t('PAL_RI_ERROR')}
          </Typography>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <Button
            onClick={handleClose}
            disabled={status === 'submitting'}
            disableElevation
            sx={{
              flex: 1,
              p: '11px 18px',
              borderRadius: '10px',
              fontSize: 14,
              fontWeight: 700,
              textTransform: 'none',
              color: '#64748b',
              bgcolor: '#fff',
              border: '1.5px solid #e2e8f0',
              '&:hover': {
                bgcolor: '#f8fafc',
                color: '#334155',
                border: '1.5px solid #e2e8f0',
              },
            }}
          >
            {t('PAL_RI_CANCEL')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={status === 'submitting'}
            disableElevation
            sx={{
              flex: 1,
              p: '11px 18px',
              borderRadius: '10px',
              fontSize: 14,
              fontWeight: 700,
              textTransform: 'none',
              color: '#fff',
              border: '1.5px solid transparent',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              transition: 'all 0.16s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(220, 38, 38, 0.32)',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              },
              '&:disabled': { opacity: 0.7, color: '#fff' },
            }}
          >
            {status === 'submitting'
              ? t('PAL_RI_SUBMITTING')
              : t('PAL_RI_SUBMIT')}
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3200}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={{
            px: '20px',
            py: '12px',
            borderRadius: '999px',
            bgcolor: '#1e293b',
            color: '#f1f5f9',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.3)',
          }}
        >
          {t('PAL_RI_TOAST')}
        </Box>
      </Snackbar>
    </>
  );
};
